import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import {
  Bell,
  Check,
  CheckCheck,
  ChevronRight,
  Clock3,
  GraduationCap,
  Inbox,
  MessageSquare,
  X,
} from 'lucide-react';
import { collection, getDocs, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import '../Notifications.scss';

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const formatFecha = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return Number.isNaN(d.getTime())
      ? ''
      : d.toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
};

const getChatPreview = (notification) => {
  if (notification.lastMessage) return notification.lastMessage;
  if (notification.lastMessageType === 'audio') return 'Mensaje de voz';
  if (notification.lastMessageType === 'image') return 'Imagen';
  if (notification.lastMessageType === 'file') return 'Archivo adjunto';
  return 'Nuevo mensaje';
};

const storageKey = (userId) => `userSeenNotifs:${userId}`;
const getSeenSet = (userId) => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return new Set(JSON.parse(raw || '[]'));
  } catch {
    return new Set();
  }
};
const setSeenSet = (userId, set) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(Array.from(set)));
  } catch {}
};

export default function NotificationsBell({ userId, isTeacher, onlyChat = false }) {
  const [open, setOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [evalNotifs, setEvalNotifs] = useState([]);
  const [chatNotifs, setChatNotifs] = useState([]);
  const [seen, setSeen] = useState(() => getSeenSet(userId));
  const panelRef = useRef(null);

  useEffect(() => {
    setSeen(getSeenSet(userId));
  }, [userId]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const previousRootOverflow = document.documentElement.style.overflow;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const handlePointerDown = (event) => {
      if (!panelRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.documentElement.style.overflow = previousRootOverflow;
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    const loadStudents = async () => {
      try {
        const usersRef = collection(db, 'users');
        const studentsQuery = query(usersRef, where('teacherId', '==', userId));
        const snap = await getDocs(studentsQuery);
        const list = snap.docs
          .map(doc => ({ id: doc.id, ...(doc.data() || {}) }))
          .filter(user => !user.teacher && !user.isMaster);
        setStudents(list);
      } catch (error) {
        console.error('Error cargando alumnos del profesor:', error);
      }
    };

    if (isTeacher && !onlyChat) loadStudents();
  }, [userId, isTeacher, onlyChat]);

  useEffect(() => {
    if (!isTeacher || onlyChat || !students.length) return undefined;

    const ids = students.map(student => student.id);
    const unsubList = chunk(ids, 10).map(ids10 => {
      const evalRef = collection(db, 'evaluaciones');
      const evaluationsQuery = query(evalRef, where('userId', 'in', ids10));
      return onSnapshot(evaluationsQuery, (snap) => {
        const arr = snap.docs.map(doc => ({ id: doc.id, type: 'eval', ...(doc.data() || {}) }));
        setEvalNotifs(prev => {
          const map = new Map(prev.map(notification => [notification.id, notification]));
          arr.forEach(notification => map.set(notification.id, notification));
          return Array.from(map.values());
        });
      }, (error) => console.error('Listener evaluaciones:', error));
    });

    return () => unsubList.forEach(unsubscribe => unsubscribe?.());
  }, [students, isTeacher, onlyChat]);

  useEffect(() => {
    if (!userId) return undefined;

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userId),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(conversationsQuery, (snapshot) => {
      const chats = snapshot.docs
        .map(doc => ({ id: doc.id, type: 'chat', ...(doc.data() || {}) }))
        .filter(conversation => (
          Array.isArray(conversation.unreadBy)
            ? conversation.unreadBy.includes(userId)
            : conversation.lastSenderId && conversation.lastSenderId !== userId
        ))
        .map(conversation => ({
          ...conversation,
          notifId: `chat_${conversation.id}_${conversation.timestamp?.toMillis?.() || ''}`,
        }));
      setChatNotifs(chats);
    });

    return () => unsubscribe();
  }, [userId]);

  const studentName = useMemo(() => {
    const names = new Map(students.map(student => [
      student.id,
      student.displayName || student.name || 'Alumno',
    ]));
    return (uid) => names.get(uid) || uid || 'Alumno';
  }, [students]);

  const allNotifs = useMemo(() => {
    const combined = [...chatNotifs];
    if (!onlyChat) combined.push(...evalNotifs);

    return combined.sort((a, b) => {
      const dateA = a?.timestamp?.toDate
        ? a.timestamp.toDate()
        : (a?.createdAt?.toDate ? a.createdAt.toDate() : new Date(a?.timestamp || a?.createdAt));
      const dateB = b?.timestamp?.toDate
        ? b.timestamp.toDate()
        : (b?.createdAt?.toDate ? b.createdAt.toDate() : new Date(b?.timestamp || b?.createdAt));
      return (dateB?.getTime?.() || 0) - (dateA?.getTime?.() || 0);
    });
  }, [chatNotifs, evalNotifs, onlyChat]);

  const unseen = allNotifs.filter(notification => !seen.has(notification.notifId || notification.id));
  const top5 = unseen.slice(0, 5);

  const markSeen = (id) => {
    const next = new Set(seen);
    next.add(id);
    setSeen(next);
    setSeenSet(userId, next);
  };

  const markAllSeen = () => {
    const next = new Set(seen);
    unseen.forEach(notification => next.add(notification.notifId || notification.id));
    setSeen(next);
    setSeenSet(userId, next);
  };

  return (
    <div className="notifications-bell" ref={panelRef}>
      <Button
        variant="link"
        className={`notifications-bell__trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen(current => !current)}
        aria-label={`${onlyChat ? 'Mensajes' : 'Notificaciones'}${unseen.length ? `, ${unseen.length} sin leer` : ''}`}
        aria-expanded={open}
      >
        <Bell size={22} aria-hidden="true" />
        {unseen.length > 0 && (
          <Badge pill className="notifications-bell__badge">
            {unseen.length > 99 ? '99+' : unseen.length}
          </Badge>
        )}
      </Button>

      {open && (
        <section
          className="notifications-popover"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notifications-popover-title"
        >
          <header className="notifications-popover__header">
            <div>
              <span className="notifications-popover__eyebrow">Centro de actividad</span>
              <h2 id="notifications-popover-title">{onlyChat ? 'Mensajes' : 'Novedades'}</h2>
              <p>
                {unseen.length === 1
                  ? '1 actualización pendiente'
                  : `${unseen.length} actualizaciones pendientes`}
              </p>
            </div>
            <div className="notifications-popover__header-actions">
              {unseen.length > 0 && (
                <Button className="notifications-mark-all" onClick={markAllSeen}>
                  <CheckCheck size={16} aria-hidden="true" />
                  <span>Marcar todas</span>
                </Button>
              )}
              <button
                type="button"
                className="notifications-popover__close"
                onClick={() => setOpen(false)}
                aria-label="Cerrar notificaciones"
              >
                <X size={22} aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="notifications-popover__list">
            {top5.map(notification => {
              if (notification.type === 'chat') {
                const sender = notification.lastSenderName || 'Chat';
                return (
                  <article key={notification.notifId} className="notification-preview notification-preview--chat">
                    <span className="notification-preview__icon" aria-hidden="true">
                      <MessageSquare size={18} />
                    </span>
                    <div className="notification-preview__content">
                      <div className="notification-preview__topline">
                        <strong>{sender}</strong>
                        <button
                          type="button"
                          className="notification-preview__seen"
                          onClick={() => markSeen(notification.notifId)}
                          aria-label={`Marcar mensaje de ${sender} como visto`}
                        >
                          <Check size={15} />
                          Visto
                        </button>
                      </div>
                      <p>{getChatPreview(notification)}</p>
                      <span className="notification-preview__date">
                        <Clock3 size={13} />
                        {formatFecha(notification.timestamp)}
                      </span>
                    </div>
                    <Button
                      as={Link}
                      to={`/chat?conversationId=${encodeURIComponent(notification.id)}`}
                      className="notification-preview__action"
                      onClick={() => {
                        markSeen(notification.notifId);
                        setOpen(false);
                      }}
                      aria-label={`Abrir chat con ${sender}`}
                    >
                      <ChevronRight size={19} />
                    </Button>
                  </article>
                );
              }

              const nombreAlumno = studentName(notification.userId);
              const nivel = notification?.nivel || '—';
              const modulo = notification?.modulo || '—';
              const nombre = notification?.nombre || 'Ejercicio';
              return (
                <article key={notification.id} className="notification-preview">
                  <span className="notification-preview__icon" aria-hidden="true">
                    <GraduationCap size={19} />
                  </span>
                  <div className="notification-preview__content">
                    <div className="notification-preview__topline">
                      <strong>{nombreAlumno}</strong>
                      <button
                        type="button"
                        className="notification-preview__seen"
                        onClick={() => markSeen(notification.id)}
                        aria-label={`Marcar actividad de ${nombreAlumno} como vista`}
                      >
                        <Check size={15} />
                        Visto
                      </button>
                    </div>
                    <p>Completó <b>{nombre}</b></p>
                    <div className="notification-preview__meta">
                      <span>Nivel {nivel}</span>
                      <span>Módulo {modulo}</span>
                    </div>
                    <span className="notification-preview__date">
                      <Clock3 size={13} />
                      {formatFecha(notification?.createdAt)}
                    </span>
                  </div>
                  <Button
                    as={Link}
                    to={`/dashboard/listado-alumnos/registro-de-practicas/${notification.userId}/ejercicio?nivel=${nivel}&modulo=${modulo}&nombre=${encodeURIComponent(nombre)}`}
                    className="notification-preview__action"
                    onClick={() => {
                      markSeen(notification.id);
                      setOpen(false);
                    }}
                    aria-label={`Ver intentos de ${nombreAlumno}`}
                  >
                    <ChevronRight size={19} />
                  </Button>
                </article>
              );
            })}

            {top5.length === 0 && (
              <div className="notifications-empty notifications-empty--compact">
                <span><Inbox size={24} /></span>
                <strong>Todo está al día</strong>
                <p>No tienes novedades pendientes.</p>
              </div>
            )}
          </div>

          {!onlyChat && (
            <Link
              to="/dashboard/notificaciones"
              className="notifications-popover__footer"
              onClick={() => setOpen(false)}
            >
              Ver centro de notificaciones
              <ChevronRight size={18} />
            </Link>
          )}
        </section>
      )}
    </div>
  );
}
